// pages/tenantBilling/TenantPaymentTab.jsx
// The tenant form's last tab, "Payment" (owner, 1 Oct 2026 —
// doc/5.10.2026.md):
//   - No fee: the organisation uses the platform free — no plan, no
//     invoices (tenant 1, the platform's own organisation, always is);
//   - otherwise the billing section: the features ticked on the Services &
//     Features tab with their prices, Monthly / Yearly, a discount, the
//     total — saving makes PLAN 1 (from today) and its unpaid invoice;
//   - what happens when the plan ends: Lock, or Message only.
//
// CREATE: everything is held by the form and sent by "Create Tenant"
// (TenantForm.submitCreate), after the features are saved — the plan prices
// the features the server stored. EDIT: the tab reads and saves on its own
// (GET/PUT /tenant-billing/:tenantId); once a plan exists, renewing and
// changing it happen on the Plan page (Tenants → Action → Plan).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AlertTriangle, BadgeIndianRupee, ExternalLink, Gift, Loader2, Save, Sparkles } from 'lucide-react';
import {
    PeriodRadio, PlanEndActionRadio, DiscountControl, PlanLines, InvoiceStatusChip, PlanPhaseChip,
} from './BillingParts';
import { planTotals, planEndFor, todayIso, day, inr } from './billingFormat';
import {
    fetchTenantBilling, saveTenantBillingSettings, quoteTenantPlan, createTenantPlan, billingErrorText,
} from '../../services/tenantBillingApi';

const Section = ({ title, hint, children, testId }) => (
    <section data-testid={testId} className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 space-y-3">
        <div>
            <h3 className="text-[15px] font-bold text-gray-800">{title}</h3>
            {hint && <p className="text-[12px] text-gray-500 mt-0.5">{hint}</p>}
        </div>
        {children}
    </section>
);

function NoFeeSwitch({ checked, onChange, locked }) {
    const { t } = useTranslation();
    return (
        <label className={`flex items-start gap-3 ${locked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
            <button type="button" role="switch" aria-checked={checked} data-testid="billing-no-fee"
                disabled={locked} onClick={() => onChange(!checked)}
                className={`relative mt-0.5 inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors ${
                    checked ? 'bg-[#4ABDBD]' : 'bg-gray-300'} ${locked ? 'opacity-60' : 'cursor-pointer'}`}>
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
            <span>
                <span className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-700">
                    <Gift className="w-4 h-4 text-[#D7A05C]" />
                    {t('tenantBilling.no_fee', 'No fee')}
                </span>
                <span className="block text-[12px] text-gray-500">
                    {locked
                        ? t('tenantBilling.no_fee_platform', 'The platform’s own organisation always uses the platform free.')
                        : t('tenantBilling.no_fee_hint', 'This organisation uses the platform free: no plan, no invoices, never locked.')}
                </span>
            </span>
        </label>
    );
}

/* ---------------- create mode: controlled by the form ---------------- */

function CreatePayment({ billing, onBillingChange, lines, discount, onDiscountChange, pricesMissing }) {
    const { t } = useTranslation();
    const priced = useMemo(
        () => lines.map((l) => ({ featureId: l.featureId, label: l.label, price: billing.period === 'yearly' ? l.yearly : l.monthly })),
        [lines, billing.period]
    );
    const totals = planTotals(priced, discount);
    const start = todayIso();
    return (
        <div className="space-y-4" data-testid="tenant-payment-tab">
            <Section title={t('tenantBilling.fee_title', 'Fee')} testId="billing-fee-section">
                <NoFeeSwitch checked={billing.noFee} onChange={(v) => onBillingChange({ noFee: v })} />
            </Section>
            {!billing.noFee && (
                <>
                    <Section title={t('tenantBilling.billing_title', 'Billing')}
                        hint={t('tenantBilling.billing_hint', 'The features ticked on the Services & Features tab, at today’s price. Super Admin only — the organisation sees its plan and invoices, never this price list.')}
                        testId="billing-section">
                        {pricesMissing && (
                            <p className="flex items-center gap-1.5 text-[12px] text-amber-700">
                                <AlertTriangle className="w-4 h-4 flex-none" />
                                {t('tenantBilling.no_prices', 'No feature prices are set (Master → Feature price) — this plan would be ₹0.')}
                            </p>
                        )}
                        <PeriodRadio value={billing.period} onChange={(period) => onBillingChange({ period })} />
                        <PlanLines lines={priced} period={billing.period} {...totals} />
                        <DiscountControl discount={discount} onChange={onDiscountChange}
                            cap={discount.type === 'percentage' ? 100 : totals.featuresTotal} />
                        <p className="text-[12px] text-gray-500" data-testid="billing-plan1-note">
                            {t('tenantBilling.plan1_note', 'Creating the organisation makes plan 1 — {{start}} to {{end}} — and its invoice (unpaid).', {
                                start: day(start), end: day(planEndFor(start, billing.period)),
                            })}
                        </p>
                    </Section>
                    <Section title={t('tenantBilling.end_title', 'When the plan ends')}
                        hint={t('tenantBilling.end_hint', 'If it is not renewed by its end date. You can change this later.')}
                        testId="billing-end-section">
                        <PlanEndActionRadio value={billing.planEndAction} onChange={(planEndAction) => onBillingChange({ planEndAction })} />
                    </Section>
                </>
            )}
        </div>
    );
}

/* ---------------- edit mode: reads and saves on its own --------------- */

function EditPayment({ tenantId, onOpenPlan }) {
    const { t } = useTranslation();
    const [summary, setSummary] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [settings, setSettings] = useState({ noFee: false, planEndAction: 'message' });
    const [period, setPeriod] = useState('monthly');
    const [discount, setDiscount] = useState({ type: null, value: '' });
    const [quote, setQuote] = useState(null);
    const [quoteError, setQuoteError] = useState(null);
    const [busy, setBusy] = useState(false);

    const adopt = useCallback((data) => {
        setSummary(data);
        if (data) setSettings({ noFee: Boolean(data.noFeeSetting || data.isOpenPlatform), planEndAction: data.planEndAction || 'message' });
    }, []);

    useEffect(() => {
        let live = true;
        fetchTenantBilling(tenantId)
            .then((data) => { if (live) adopt(data); })
            .catch((err) => { if (live) setLoadError(billingErrorText(err, t('tenantBilling.load_error', 'Could not load this organisation’s billing.'))); });
        return () => { live = false; };
    }, [tenantId, adopt, t]);

    const hasPlan = Boolean(summary?.plans?.some((p) => p.phase !== 'cancelled'));
    // Plan 1 can be priced only once the SAVED setting is "fee" — a No fee
    // organisation is refused by the server.
    const wantsPlan1 = Boolean(summary) && !settings.noFee && !summary?.noFee && !hasPlan;
    const saveFirst = Boolean(summary) && !settings.noFee && summary?.noFee && !hasPlan;

    // Plan 1 is priced by the server from the SAVED features — exactly what
    // it will store. Re-asked as the period or the discount changes.
    useEffect(() => {
        if (!wantsPlan1) { setQuote(null); return undefined; }
        let live = true;
        const timer = setTimeout(() => {
            quoteTenantPlan(tenantId, {
                mode: 'first', period, discountType: discount.type, discountValue: Number(discount.value) || 0,
            })
                .then((q) => { if (live) { setQuote(q); setQuoteError(null); } })
                .catch((err) => { if (live) setQuoteError(billingErrorText(err, t('tenantBilling.quote_error', 'Could not price the plan.'))); });
        }, 250);
        return () => { live = false; clearTimeout(timer); };
    }, [wantsPlan1, tenantId, period, discount.type, discount.value, t]);

    const settingsChanged = summary && (
        Boolean(summary.noFeeSetting || summary.isOpenPlatform) !== settings.noFee
        || (summary.planEndAction || 'message') !== settings.planEndAction
    );

    const saveSettings = async () => {
        setBusy(true);
        try {
            adopt(await saveTenantBillingSettings(tenantId, settings));
            toast.success(t('tenantBilling.settings_saved', 'Payment settings saved'));
        } catch (err) {
            toast.error(billingErrorText(err, t('tenantBilling.save_error', 'Could not save.')));
        } finally {
            setBusy(false);
        }
    };

    const makePlan1 = async () => {
        setBusy(true);
        try {
            if (settingsChanged) adopt(await saveTenantBillingSettings(tenantId, settings));
            const res = await createTenantPlan(tenantId, {
                mode: 'first', period, discountType: discount.type, discountValue: Number(discount.value) || 0,
            });
            adopt(res?.data || null);
            toast.success(t('tenantBilling.plan1_created', 'Plan 1 created, with its invoice'));
        } catch (err) {
            toast.error(billingErrorText(err, t('tenantBilling.save_error', 'Could not save.')));
        } finally {
            setBusy(false);
        }
    };

    if (loadError) {
        return <p className="text-sm text-red-600" data-testid="tenant-payment-error">{loadError}</p>;
    }
    if (!summary) {
        return (
            <p className="flex items-center gap-2 text-sm text-gray-500" data-testid="tenant-payment-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> {t('tenantBilling.loading', 'Loading…')}
            </p>
        );
    }

    const state = summary.state || {};
    const current = summary.currentPlan;
    const currentInvoice = current ? summary.invoices.find((i) => i.tenantPlanId === current.tenantPlanId) : null;

    return (
        <div className="space-y-4" data-testid="tenant-payment-tab">
            <Section title={t('tenantBilling.fee_title', 'Fee')} testId="billing-fee-section">
                <NoFeeSwitch checked={settings.noFee} locked={summary.isOpenPlatform}
                    onChange={(v) => setSettings((s) => ({ ...s, noFee: v }))} />
            </Section>

            {!settings.noFee && hasPlan && (
                <Section title={t('tenantBilling.plan_title', 'Plan')} testId="billing-plan-summary">
                    {state.kind === 'ended' && (
                        <p className="flex items-center gap-1.5 text-[12px] font-semibold text-[#8a5a1a]" data-testid="billing-state-ended">
                            <AlertTriangle className="w-4 h-4" />
                            {state.action === 'lock'
                                ? t('tenantBilling.state_ended_lock', 'The plan ended on {{date}} — the organisation is locked until it is renewed.', { date: day(state.endedOn) })
                                : t('tenantBilling.state_ended_message', 'The plan ended on {{date}} — they see a renewal banner.', { date: day(state.endedOn) })}
                        </p>
                    )}
                    {current ? (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-gray-700">
                            <span className="font-semibold">{t('tenantBilling.plan_n', 'Plan {{n}}', { n: current.planNo })}</span>
                            <PlanPhaseChip phase={current.phase} />
                            <span>{current.period === 'yearly' ? t('tenantBilling.yearly', 'Yearly') : t('tenantBilling.monthly', 'Monthly')}</span>
                            <span>{day(current.startDate)} – {day(current.coveredUntil || current.endDate)}</span>
                            <span className="font-semibold text-[#2D8A8A]">{inr(current.total)}</span>
                            {currentInvoice && <InvoiceStatusChip status={currentInvoice.status} />}
                        </div>
                    ) : null}
                    {state.kind === 'active' && state.renewed && (
                        <p className="text-[12px] text-gray-500">{t('tenantBilling.renewal_from', 'Renewed — the next plan starts on {{date}}.', { date: day(state.renewalStartsOn) })}</p>
                    )}
                    <button type="button" data-testid="billing-open-plan" onClick={onOpenPlan}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] text-[13px] font-semibold hover:bg-[#4ABDBD]/5 cursor-pointer">
                        <ExternalLink className="w-4 h-4" />
                        {t('tenantBilling.open_plan', 'Open plan — renew, change, invoices')}
                    </button>
                </Section>
            )}

            {saveFirst && (
                <p className="text-[12px] text-gray-500" data-testid="billing-save-first">
                    {t('tenantBilling.save_first', 'Save the payment settings to start billing this organisation; plan 1 is made here afterwards.')}
                </p>
            )}

            {wantsPlan1 && (
                <Section title={t('tenantBilling.billing_title', 'Billing')}
                    hint={t('tenantBilling.plan1_edit_hint', 'No plan yet. Plan 1 prices the SAVED Services & Features at today’s price — save that tab first if you changed it.')}
                    testId="billing-section">
                    <PeriodRadio value={period} onChange={setPeriod} />
                    {quoteError && <p className="text-[12px] text-red-600">{quoteError}</p>}
                    {quote ? (
                        <PlanLines lines={quote.features} period={period} featuresTotal={quote.featuresTotal}
                            discountAmount={quote.discountAmount} total={quote.total} />
                    ) : (
                        <p className="flex items-center gap-2 text-[12px] text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> {t('tenantBilling.pricing', 'Pricing…')}</p>
                    )}
                    <DiscountControl discount={discount} onChange={setDiscount}
                        cap={discount.type === 'percentage' ? 100 : (quote?.featuresTotal ?? Infinity)} />
                    {quote && (
                        <p className="text-[12px] text-gray-500">
                            {t('tenantBilling.plan1_dates', 'Plan 1 runs {{start}} to {{end}}; its invoice starts unpaid.', { start: day(quote.startDate), end: day(quote.endDate) })}
                        </p>
                    )}
                    <button type="button" data-testid="billing-create-plan1" onClick={makePlan1} disabled={busy || !quote}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white text-[13px] font-semibold shadow cursor-pointer disabled:opacity-50">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeIndianRupee className="w-4 h-4" />}
                        {t('tenantBilling.create_plan1', 'Create plan 1')}
                    </button>
                </Section>
            )}

            {!settings.noFee && (
                <Section title={t('tenantBilling.end_title', 'When the plan ends')}
                    hint={t('tenantBilling.end_hint', 'If it is not renewed by its end date. You can change this later.')}
                    testId="billing-end-section">
                    <PlanEndActionRadio value={settings.planEndAction} onChange={(planEndAction) => setSettings((s) => ({ ...s, planEndAction }))} />
                </Section>
            )}

            {summary.isOpenPlatform && (
                <p className="flex items-center gap-1.5 text-[12px] text-gray-500">
                    <Sparkles className="w-4 h-4 text-[#D7A05C]" />
                    {t('tenantBilling.platform_note', 'Mitra One Care platform organisation — never billed.')}
                </p>
            )}

            {!summary.isOpenPlatform && (
                <button type="button" data-testid="billing-save-settings" onClick={saveSettings} disabled={busy || !settingsChanged}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-300 text-gray-700 text-[13px] font-semibold hover:border-[#4ABDBD] hover:text-[#2D8A8A] cursor-pointer disabled:opacity-50">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {t('tenantBilling.save_settings', 'Save payment settings')}
                </button>
            )}
        </div>
    );
}

export default function TenantPaymentTab(props) {
    return props.mode === 'edit'
        ? <EditPayment tenantId={props.tenantId} onOpenPlan={props.onOpenPlan} />
        : <CreatePayment {...props} />;
}
